import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

/**
 * The processor options our standalone (Basis Theory) 3DS reads when a processor charges with an
 * external 3DS result (`src/lib/payment/auth/threeds/basisTheory.ts`). Same fields and keys as the
 * NMI form, which predates this component.
 */
export interface Universal3dsSettingsValue {
  enable3DS: boolean
  merchantName: string
  merchantUrl: string
  merchantCountryCode: string
  amexRequestorType: string
  cbSiretNumber: string
  discoverClientId: string
  acquirerBinVisa: string
  acquirerBinMastercard: string
  acquirerBinAmex: string
  acquirerBinDiscover: string
  acquirerBinJcb: string
  acquirerBinDinersClub: string
  acquirerBinCB: string
}

const ACQUIRER_BIN_FIELDS = [
  ['acquirerBinVisa', 'visaAcquirerBin'],
  ['acquirerBinMastercard', 'mastercardAcquirerBin'],
  ['acquirerBinAmex', 'amexAcquirerBin'],
  ['acquirerBinDiscover', 'discoverAcquirerBin'],
  ['acquirerBinJcb', 'jcbAcquirerBin'],
  ['acquirerBinDinersClub', 'dinersClubAcquirerBin'],
  ['acquirerBinCB', 'cbAcquirerBin'],
] as const satisfies ReadonlyArray<readonly [keyof Universal3dsSettingsValue, string]>

const AMEX_REQUESTOR_TYPES = [
  { value: 'NONE', label: 'Not applicable' },
  { value: 'MER', label: 'MER - General Merchant' },
  { value: 'AGG', label: 'AGG - Aggregator' },
  { value: 'JCB', label: 'JCB - JCB-Acquired Merchant' },
  { value: 'OTA', label: 'OTA - Online Travel Agency' },
  { value: 'OPT', label: 'OPT - OptBlue Seller' },
  { value: 'WAL', label: 'WAL - Digital Wallet' },
]

const readString = (options: Record<string, unknown> | null | undefined, key: string): string => {
  const value = options?.[key]
  return typeof value === 'string' ? value : ''
}

/** The form state from a processor's stored options. */
export const universal3dsSettingsFromOptions = (
  options: Record<string, unknown> | null | undefined,
): Universal3dsSettingsValue => ({
  enable3DS: options?.enable3DS === true,
  merchantName: readString(options, 'merchantName'),
  merchantUrl: readString(options, 'merchantUrl'),
  merchantCountryCode: readString(options, 'merchantCountryCode'),
  amexRequestorType: readString(options, 'amexRequestorType') || 'NONE',
  cbSiretNumber: readString(options, 'cbSiretNumber'),
  discoverClientId: readString(options, 'discoverClientId'),
  acquirerBinVisa: readString(options, 'acquirerBinVisa'),
  acquirerBinMastercard: readString(options, 'acquirerBinMastercard'),
  acquirerBinAmex: readString(options, 'acquirerBinAmex'),
  acquirerBinDiscover: readString(options, 'acquirerBinDiscover'),
  acquirerBinJcb: readString(options, 'acquirerBinJcb'),
  acquirerBinDinersClub: readString(options, 'acquirerBinDinersClub'),
  acquirerBinCB: readString(options, 'acquirerBinCB'),
})

/** The options to save. `NONE` is how the select says "no Amex requestor type"; the back end stores ''. */
export const universal3dsSettingsToOptions = (value: Universal3dsSettingsValue) => ({
  ...value,
  amexRequestorType: value.amexRequestorType === 'NONE' ? '' : value.amexRequestorType,
})

/** 3DS needs at least one acquirer BIN to authenticate against. */
export const hasAcquirerBin = (value: Universal3dsSettingsValue): boolean =>
  ACQUIRER_BIN_FIELDS.some(([field]) => value[field].trim() !== '')

interface Universal3dsSettingsProps {
  value: Universal3dsSettingsValue
  onChange: (value: Universal3dsSettingsValue) => void
}

export const Universal3dsSettings = ({ value, onChange }: Universal3dsSettingsProps) => {
  const { t } = useTranslation('processors')
  const set = <K extends keyof Universal3dsSettingsValue>(key: K, next: Universal3dsSettingsValue[K]) =>
    onChange({ ...value, [key]: next })

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>3D Secure</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="enable3DS"
              checked={value.enable3DS}
              onCheckedChange={(checked) => set('enable3DS', checked === true)}
            />
            <Label htmlFor="enable3DS">{t('enableUniversal3DS')}</Label>
          </div>
          {value.enable3DS && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <p className="text-sm text-blue-700">{t('universal3DSDescription')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {value.enable3DS && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{t('universal3DSConfiguration')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="merchantName">{t('merchantName')}</Label>
                <Input
                  id="merchantName"
                  value={value.merchantName}
                  onChange={(e) => set('merchantName', e.target.value)}
                  placeholder={t('merchantNamePlaceholder')}
                />
                <p className="text-muted-foreground mt-1 text-xs">{t('merchantNameDescription')}</p>
              </div>
              <div>
                <Label htmlFor="merchantUrl">{t('merchantUrl')}</Label>
                <Input
                  id="merchantUrl"
                  value={value.merchantUrl}
                  onChange={(e) => set('merchantUrl', e.target.value)}
                  placeholder="https://www.yourcompany.com"
                />
                <p className="text-muted-foreground mt-1 text-xs">{t('merchantUrlDescription')}</p>
              </div>
              <div>
                <Label htmlFor="merchantCountryCode">{t('merchantCountryCode')}</Label>
                <Input
                  id="merchantCountryCode"
                  value={value.merchantCountryCode}
                  onChange={(e) => set('merchantCountryCode', e.target.value)}
                  placeholder="e.g., 840 for USA, 250 for France"
                  maxLength={3}
                />
                <p className="text-muted-foreground mt-1 text-xs">{t('merchantCountryCodeDescription')}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('networkSpecificInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="amexRequestorType">{t('amexRequestorType')}</Label>
                <Select
                  value={value.amexRequestorType || 'NONE'}
                  onValueChange={(next) => set('amexRequestorType', next)}
                >
                  <SelectTrigger id="amexRequestorType">
                    <SelectValue placeholder={t('selectAmexRequestorType')} />
                  </SelectTrigger>
                  <SelectContent>
                    {AMEX_REQUESTOR_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground mt-1 text-xs">{t('amexRequestorTypeDescription')}</p>
              </div>
              <div>
                <Label htmlFor="cbSiretNumber">{t('cbSiretNumber')}</Label>
                <Input
                  id="cbSiretNumber"
                  value={value.cbSiretNumber}
                  onChange={(e) => set('cbSiretNumber', e.target.value)}
                  placeholder="e.g., 12345678901234"
                />
                <p className="text-muted-foreground mt-1 text-xs">{t('cbSiretNumberDescription')}</p>
              </div>
              <div>
                <Label htmlFor="discoverClientId">{t('discoverClientId')}</Label>
                <Input
                  id="discoverClientId"
                  value={value.discoverClientId}
                  onChange={(e) => set('discoverClientId', e.target.value)}
                  placeholder="e.g., DISC12345"
                />
                <p className="text-muted-foreground mt-1 text-xs">{t('discoverClientIdDescription')}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('acquirerBINs')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {ACQUIRER_BIN_FIELDS.map(([field, labelKey]) => (
                <div key={field}>
                  <Label htmlFor={field}>{t(labelKey)}</Label>
                  <Input
                    id={field}
                    value={value[field]}
                    onChange={(e) => set(field, e.target.value)}
                    placeholder="e.g., 123456"
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </>
  )
}
